// 各マスタ用の MasterCrudConfig 集合体。
// area系マスタ: `area_division_id` を持つ。プルダウンは AreaDivision から動的生成。
// material系マスタ: `material_division_id` を持つ。本フェーズは「共通材料」固定運用。
//
// 全マスタは API 側 schema.json の zod 生成型に整合させる(deleted_at, is_active 等)。

import { useEffect, useRef } from "react";
import { Autocomplete, Box, Chip, FormControlLabel, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import { defineMasterConfig, type MasterDraftTransients, type MasterCrudConfig } from "./MasterCrudPage";
import type { ExtendedDB } from "../../../types/extendedDB";
import type { AreaDivisionLocationLabelBase } from "../../../schema/generated/AreaDivisionLocationLabel";
import type { LocationDivisionBase } from "../../../schema/generated/LocationDivision";
import type { QualityMeasurementItemBase } from "../../../schema/generated/QualityMeasurementItem";
import { WORD } from "../../../word";

// 未削除のレコードを判定する
function isActive(r: { deleted_at: number | null }): boolean {
  return r.deleted_at === null;
}

// 工事型区分マスタから area プルダウン選択肢を動的生成
const areaSelectFromDb = (db: ExtendedDB): { value: number; label: string }[] =>
  db.areaDivisions
    .filter((d) => d.is_active && isActive(d))
    .sort((a, b) => a.display_order - b.display_order)
    .map((d) => ({ value: d.id, label: `#${d.id} ${d.name}` }));

const materialSelectFromDb = (db: ExtendedDB): { value: number; label: string }[] =>
  db.materialDivisions
    .filter((d) => d.is_active && isActive(d))
    .sort((a, b) => a.display_order - b.display_order)
    .map((d) => ({ value: d.id, label: `#${d.id} ${d.name}` }));

const yesNo = (v: boolean) => <Chip size="small" color={v ? "success" : "default"} label={v ? "ON" : "OFF"} />;

// 配合比範囲の文字列化(終了値が無ければ開始値のみ)
const formatRatioRange = (start: unknown, end: unknown, separator: string) => {
  if (end != null) {
    return `${start}${separator}${end}`;
  }
  return `${start}`;
};

// 工事型区分の編集ドラフトに乗る一時フィールド(配下の場所区分ラベル設定)。
// AreaDivisionBase には存在しない transient プロパティで、保存時に
// areaDivisionLocationLabels コレクションへ反映される。
type LocationLabelDraft = Pick<AreaDivisionLocationLabelBase, "slot" | "label_name" | "is_required">;

// ===== 工事型区分マスタ =====
// 場所区分ラベル設定(1〜10 スロット)を customEditContent でインライン編集する。
export const areaDivisionConfig = defineMasterConfig({
  collectionKey: "areaDivisions",
  title: "工事型区分",
  description: "案件・現場単位の仕切りキー。工事マスタから自動で引き継がれ、関連するマスタの絞り込みに使う。場所区分ラベル設定もここで管理。",
  idPrefix: "ad",
  newDefaults: () => ({ name: "", description: "", display_order: 99, is_active: true }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "name", label: "名前" },
    { key: "description", label: "説明" },
    { key: "display_order", label: "表示順", width: 80 },
    {
      key: "location_labels", label: "場所ラベル", width: 200,
      render: (r, db) => {
        const labels = (db.areaDivisionLocationLabels ?? [])
          .filter((l) => l.area_division_id === r.id && l.deleted_at == null && l.label_name);
        if (labels.length === 0) {
          return <Typography variant="caption" color="text.secondary">未設定</Typography>;
        }
        const sorted = [...labels].sort((a, b) => a.slot - b.slot);
        return (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {sorted.slice(0, 3).map((l) => (
              <Chip key={l.slot} size="small" variant="outlined" label={`${l.slot}:${l.label_name}`} />
            ))}
            {sorted.length > 3 && <Chip size="small" label={`+${sorted.length - 3}`} />}
          </Stack>
        );
      },
    },
    { key: "is_active", label: "有効", width: 80, render: (r) => yesNo(r.is_active) },
  ],
  fields: [
    { key: "name", label: "名前", type: "text", required: true },
    { key: "description", label: "説明", type: "multiline" },
    { key: "display_order", label: "表示順", type: "number" },
    { key: "is_active", label: "有効", type: "boolean" },
  ],
  // 場所区分ラベル設定 (1〜10 スロット)。draft.location_labels に一時保存され、
  // 保存時に MasterCrudPage 側で areaDivisionLocationLabels コレクションへ同期される。
  customEditContent: ({ draft, setDraft }) => {
    const labels: LocationLabelDraft[] = draft.location_labels ?? [];
    const labelBySlot = new Map<number, LocationLabelDraft>();
    for (const l of labels) {
      labelBySlot.set(l.slot, l);
    }
    const updateSlot = (slot: number, patch: Partial<LocationLabelDraft>) => {
      const next: LocationLabelDraft[] = [];
      for (let s = 1; s <= 10; s++) {
        const cur = labelBySlot.get(s);
        if (s === slot) {
          const merged: LocationLabelDraft = {
            slot,
            label_name: cur?.label_name ?? "",
            is_required: cur?.is_required ?? false,
            ...patch,
          };
          next.push(merged);
        } else if (cur) {
          next.push(cur);
        } else {
          next.push({ slot: s, label_name: "", is_required: false });
        }
      }
      setDraft({ ...draft, location_labels: next });
    };
    return (
      <Box>
        <Typography variant="subtitle2" mb={1}>場所区分ラベル設定（この工事型区分配下の工事で使われる）</Typography>
        <Typography variant="caption" color="text.secondary" mb={1} display="block">
          {WORD.mastersConfigs.areaDivisionLabelsInfo}
        </Typography>
        <Stack gap={1}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((slot) => {
            const cur = labelBySlot.get(slot);
            return (
              <Stack key={slot} direction="row" gap={1.5} alignItems="center">
                <Typography variant="body2" sx={{ minWidth: 70 }}>区分{slot}</Typography>
                <TextField
                  size="small"
                  placeholder="例: トンネル名"
                  sx={{ flex: 1 }}
                  value={cur?.label_name ?? ""}
                  onChange={(e) => updateSlot(slot, { label_name: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={!!cur?.is_required}
                      onChange={(e) => updateSlot(slot, { is_required: e.target.checked })}
                    />
                  }
                  label="必須"
                />
              </Stack>
            );
          })}
        </Stack>
      </Box>
    );
  },
});

// ===== 材料区分マスタ =====
export const materialDivisionConfig = defineMasterConfig({
  collectionKey: "materialDivisions",
  title: "材料区分",
  description: "材料の体系区分。本フェーズは「共通材料」で固定。",
  idPrefix: "md",
  newDefaults: () => ({ name: "", description: "", display_order: 99, is_active: true }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "name", label: "名前" },
    { key: "description", label: "説明" },
    { key: "display_order", label: "表示順", width: 80 },
    { key: "is_active", label: "有効", width: 80, render: (r) => yesNo(r.is_active) },
  ],
  fields: [
    { key: "name", label: "名前", type: "text", required: true },
    { key: "description", label: "説明", type: "multiline" },
    { key: "display_order", label: "表示順", type: "number" },
    { key: "is_active", label: "有効", type: "boolean" },
  ],
});

// ===== 発注者マスタ =====
export const clientConfig = defineMasterConfig({
  collectionKey: "clients",
  title: "発注者",
  description: "発注者組織(首都高速道路 等)。",
  idPrefix: "cl",
  newDefaults: () => ({ name: "" }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "name", label: "名前" },
  ],
  fields: [{ key: "name", label: "名前", type: "text", required: true }],
});

// ===== 工種マスタ =====
export const workTypeConfig = defineMasterConfig({
  collectionKey: "workTypes",
  title: "工種",
  idPrefix: "wt",
  newDefaults: () => ({ area_division_id: 0, name: "", display_order: 99 }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "area_division_id", label: "工事型区分", width: 160, render: (r, db) => {
      const area = db.areaDivisions.find((d) => d.id === r.area_division_id);
      return area ? `#${area.id} ${area.name}` : `#${r.area_division_id}`;
    }},
    { key: "name", label: "名前" },
    { key: "display_order", label: "表示順", width: 80 },
  ],
  fields: [
    { key: "area_division_id", label: "工事型区分", type: "select", required: true, options: areaSelectFromDb },
    { key: "name", label: "名前", type: "text", required: true },
    { key: "display_order", label: "表示順", type: "number" },
  ],
});

// ===== 種別マスタ =====
export const workSubtypeConfig = defineMasterConfig({
  collectionKey: "workSubtypes",
  title: "種別",
  idPrefix: "ws",
  newDefaults: () => ({ area_division_id: 0, work_type_id: 0, name: "", unit: "", primary_work_detail_id: null }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "area_division_id", label: "工事型区分", width: 140, render: (r, db) => {
      const area = db.areaDivisions.find((d) => d.id === r.area_division_id);
      return area ? `#${area.id} ${area.name}` : `#${r.area_division_id}`;
    }},
    { key: "work_type_id", label: "工種", width: 140, render: (r, db) => {
      const wt = db.workTypes.find((d) => d.id === r.work_type_id);
      return wt ? `#${wt.id} ${wt.name}` : `#${r.work_type_id}`;
    }},
    { key: "name", label: "名前" },
    { key: "unit", label: "単位", width: 80 },
  ],
  fields: [
    { key: "area_division_id", label: "工事型区分", type: "select", required: true, options: areaSelectFromDb },
    { key: "work_type_id", label: "工種", type: "select", required: true, options: (db, draft) => {
      const areaId = draft?.area_division_id;
      return db.workTypes
        .filter((w) => isActive(w) && (areaId === undefined || w.area_division_id === areaId))
        .map((w) => ({ value: w.id, label: `#${w.id} ${w.name}` }));
    }},
    { key: "name", label: "名前", type: "text", required: true },
    { key: "unit", label: "単位", type: "text" },
  ],
});

// ===== 工程マスタ =====
export const workDetailConfig = defineMasterConfig({
  collectionKey: "workDetails",
  title: "工程",
  idPrefix: "wd",
  newDefaults: () => ({ area_division_id: 0, work_subtype_id: 0, name: "", mix_ratio_definition_id: null }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "area_division_id", label: "工事型区分", width: 140, render: (r, db) => {
      const area = db.areaDivisions.find((d) => d.id === r.area_division_id);
      return area ? `#${area.id} ${area.name}` : `#${r.area_division_id}`;
    }},
    { key: "work_subtype_id", label: "種別", width: 140, render: (r, db) => {
      const ws = db.workSubtypes.find((d) => d.id === r.work_subtype_id);
      return ws ? `#${ws.id} ${ws.name}` : `#${r.work_subtype_id}`;
    }},
    { key: "name", label: "名前" },
    { key: "mix_ratio_definition_id", label: "配合比", width: 140, render: (r, db) => {
      if (r.mix_ratio_definition_id == null) return "-";
      const mr = db.mixRatioDefinitions.find((d) => d.id === r.mix_ratio_definition_id);
      return mr ? `#${mr.id}` : `#${r.mix_ratio_definition_id}`;
    }},
  ],
  fields: [
    { key: "area_division_id", label: "工事型区分", type: "select", required: true, options: areaSelectFromDb },
    { key: "work_subtype_id", label: "種別", type: "select", required: true, options: (db, draft) => {
      const areaId = draft?.area_division_id;
      return db.workSubtypes
        .filter((w) => isActive(w) && (areaId === undefined || w.area_division_id === areaId))
        .map((w) => ({ value: w.id, label: `#${w.id} ${w.name}` }));
    }},
    { key: "name", label: "名前", type: "text", required: true },
    { key: "mix_ratio_definition_id", label: "配合比", type: "select", options: (db) =>
      [{ value: "", label: "(なし)" }, ...db.mixRatioDefinitions
        .filter(isActive)
        .map((m) => ({ value: m.id, label: `#${m.id}` }))]
    },
  ],
});

// ===== 搬入場所マスタ =====
export const carryInLocationConfig = defineMasterConfig({
  collectionKey: "carryInLocations",
  title: "搬入場所",
  idPrefix: "cil",
  newDefaults: () => ({ area_division_id: 0, name: "" }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "area_division_id", label: "工事型区分", width: 160, render: (r, db) => {
      const area = db.areaDivisions.find((d) => d.id === r.area_division_id);
      return area ? `#${area.id} ${area.name}` : `#${r.area_division_id}`;
    }},
    { key: "name", label: "名前" },
  ],
  fields: [
    { key: "area_division_id", label: "工事型区分", type: "select", required: true, options: areaSelectFromDb },
    { key: "name", label: "名前", type: "text", required: true },
  ],
});

// 工事型区分のスロットラベル(場所区分ラベル設定から解決)。未設定なら「区分N」既定名。
function slotLabelFor(db: ExtendedDB, areaDivisionId: number | undefined, slot: number | undefined): string {
  if (areaDivisionId == null || slot == null) {
    return `区分${slot ?? "?"}`;
  }
  const label = db.areaDivisionLocationLabels.find(
    (l) => l.area_division_id === areaDivisionId && l.slot === slot && l.deleted_at == null,
  );
  if (label?.label_name) {
    return label.label_name;
  }
  return `区分${slot}`;
}

// 場所区分マスタの編集ドラフト(上位区分パス parent_path_draft は MasterDraftTransients 由来の
// 一時フィールドで、保存時に本体から除去し locationDivisionParentPaths コレクションへ反映される)。
type LocationDivisionDraft = LocationDivisionBase & MasterDraftTransients;

// 場所区分の既存 parent_path が draft のパス条件(自分より上位のスロット指定)に合致するか判定する。
function matchesParentPath(db: ExtendedDB, candidate: LocationDivisionBase, path: Record<number, string>): boolean {
  const candidatePaths = db.locationDivisionParentPaths
    .filter((p) => p.location_division_id === candidate.id && p.deleted_at == null);
  const pathBySlot = new Map<number, string>();
  for (const p of candidatePaths) {
    pathBySlot.set(p.ancestor_slot, p.ancestor_division_name);
  }
  for (const [slotStr, name] of Object.entries(path)) {
    const slot = Number(slotStr);
    if (pathBySlot.get(slot) !== name) {
      return false;
    }
  }
  return true;
}

// 上位区分パス編集 UI(mock ParentPathEditor 移植)。web は parent_path を本体に持たず、
// draft.parent_path_draft(一時フィールド)で編集し、保存時に MasterCrudPage 側で
// locationDivisionParentPaths コレクションへ反映する。
function ParentPathEditor({ draft, setDraft, db }: { draft: LocationDivisionDraft; setDraft: (next: LocationDivisionDraft) => void; db: ExtendedDB }) {
  const path = draft.parent_path_draft ?? {};

  // スロット変更で自スロット以下のエントリが残った場合は除去する。
  // 工事型区分を変更した場合は、パスが旧区分の区分名を指したまま残らないよう全クリアする。
  const prevAreaRef = useRef(draft.area_division_id);
  useEffect(() => {
    const areaChanged = prevAreaRef.current !== draft.area_division_id;
    prevAreaRef.current = draft.area_division_id;
    if (areaChanged) {
      if (draft.parent_path_draft) setDraft({ ...draft, parent_path_draft: undefined });
      return;
    }
    const stale = Object.keys(path).filter((k) => Number(k) >= draft.slot);
    if (stale.length === 0) return;
    const next = { ...path };
    for (const k of stale) delete next[Number(k)];
    setDraft({ ...draft, parent_path_draft: Object.keys(next).length > 0 ? next : undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.slot, draft.area_division_id]);

  const upperSlots = Array.from({ length: Math.max(0, draft.slot - 1) }, (_, i) => i + 1);
  if (upperSlots.length === 0) {
    return <Typography variant="caption" color="text.secondary">{WORD.mastersConfigs.locationDivisionTopSlot}</Typography>;
  }

  const setEntry = (slot: number, v: string) => {
    const next = { ...path };
    const trimmed = v.trim();
    if (trimmed) next[slot] = trimmed;
    else delete next[slot];
    setDraft({ ...draft, parent_path_draft: Object.keys(next).length > 0 ? next : undefined });
  };

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">上位区分のパス</Typography>
      <Typography variant="caption" color="text.secondary">{WORD.mastersConfigs.hLocationDivisionParentPath}</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5 }}>
        {upperSlots.map((s) => {
          // スロット s の候補は「s より上位(slot < s)の祖先条件」のみで絞り込む。
          // path に s 以降のスロットが(順不同入力等で)残っていても、それらは s の候補選定に影響させない。
          const ancestorPath = Object.fromEntries(
            Object.entries(path).filter(([slotStr]) => Number(slotStr) < s)
          );
          const opts = Array.from(new Set(
            db.locationDivisions
              .filter((ld) => ld.deleted_at == null && ld.area_division_id === draft.area_division_id && ld.slot === s)
              .filter((ld) => matchesParentPath(db, ld, ancestorPath))
              .sort((a, b) => a.display_order - b.display_order)
              .map((ld) => ld.division_name)
          ));
          return (
            <Autocomplete key={s}
              freeSolo
              size="small"
              options={opts}
              value={path[s] ?? ""}
              onChange={(_, v) => setEntry(s, v ?? "")}
              onInputChange={(_, v) => setEntry(s, v)}
              renderInput={(params) => <TextField {...params} label={slotLabelFor(db, draft.area_division_id, s)} />}
            />
          );
        })}
      </Box>
    </Stack>
  );
}

// ===== 場所区分マスタ =====
export const locationDivisionConfig = defineMasterConfig({
  collectionKey: "locationDivisions",
  title: "場所区分",
  description: "場所区分の選択肢。上位区分のパスを指定すると階層構造になり、施工箇所登録で上位の選択値に連動して候補が絞り込まれる。工事型区分マスタの場所区分ラベル設定で意味付けする。",
  idPrefix: "ld",
  newDefaults: () => ({ area_division_id: 0, slot: 1, division_name: "", display_order: 99 }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "area_division_id", label: "工事型区分", width: 140, render: (r, db) => {
      const area = db.areaDivisions.find((d) => d.id === r.area_division_id);
      return area ? `#${area.id} ${area.name}` : `#${r.area_division_id}`;
    }},
    { key: "slot", label: "区分スロット", width: 140, render: (r, db) => slotLabelFor(db, r.area_division_id, r.slot) },
    {
      key: "parent_path", label: "上位区分パス",
      render: (r, db) => {
        const paths = db.locationDivisionParentPaths
          .filter((p) => p.location_division_id === r.id && p.deleted_at == null)
          .sort((a, b) => a.ancestor_slot - b.ancestor_slot);
        if (paths.length === 0) {
          return <Typography variant="caption" color="text.secondary">指定なし</Typography>;
        }
        return paths.map((p) => p.ancestor_division_name).join(" / ");
      },
    },
    { key: "division_name", label: "区分名" },
    { key: "display_order", label: "表示順", width: 80 },
  ],
  fields: [
    {
      key: "slot", label: "区分スロット", type: "custom",
      customRender: ({ draft, setDraft, db }) => (
        <TextField select fullWidth size="small" label="区分スロット" required
          value={draft.slot ?? ""}
          onChange={(e) => setDraft({ ...draft, slot: Number(e.target.value) })}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((s) => (
            <MenuItem key={s} value={s}>{slotLabelFor(db, draft.area_division_id, s)}</MenuItem>
          ))}
        </TextField>
      ),
    },
    { key: "division_name", label: "区分名", type: "text", required: true },
    {
      key: "parent_path", label: "上位区分のパス", type: "custom",
      customRender: ({ draft, setDraft, db }) => <ParentPathEditor draft={draft} setDraft={setDraft} db={db} />,
    },
    { key: "display_order", label: "表示順", type: "number" },
    { key: "area_division_id", label: "工事型区分", type: "select", required: true, options: areaSelectFromDb },
  ],
});

// 品質測定項目の紐付け select 用オプション。scope_kind に応じて対応マスタから {value:id, label:name} を生成する。
const materialLinkOptions = (db: ExtendedDB): { value: number; label: string }[] =>
  db.materials.filter(isActive).map((m) => ({ value: m.id, label: `#${m.id} ${m.name}` }));
const workTypeLinkOptions = (db: ExtendedDB): { value: number; label: string }[] =>
  db.workTypes.filter(isActive).map((w) => ({ value: w.id, label: `#${w.id} ${w.name}` }));
const workSubtypeLinkOptions = (db: ExtendedDB): { value: number; label: string }[] =>
  db.workSubtypes.filter(isActive).map((w) => ({ value: w.id, label: `#${w.id} ${w.name}` }));
const workDetailLinkOptions = (db: ExtendedDB): { value: number; label: string }[] =>
  db.workDetails.filter(isActive).map((w) => ({ value: w.id, label: `#${w.id} ${w.name}` }));

// 品質測定項目の「適用対象」列。scope_kind と紐付け先マスタ名を解決して表示する。
const qualityScopeLabel = (r: QualityMeasurementItemBase, db: ExtendedDB): string => {
  if (r.scope_kind === "material") {
    const m = db.materials.find((x) => x.id === r.material_id);
    return `材料別: ${m ? m.name : "(未設定)"}`;
  }
  if (r.scope_kind === "work_type") {
    const wt = db.workTypes.find((x) => x.id === r.work_type_id);
    return `工種別: ${wt ? wt.name : "(未設定)"}`;
  }
  if (r.scope_kind === "work_subtype") {
    const ws = db.workSubtypes.find((x) => x.id === r.work_subtype_id);
    return `種別別: ${ws ? ws.name : "(未設定)"}`;
  }
  if (r.scope_kind === "work_detail") {
    const wd = db.workDetails.find((x) => x.id === r.work_detail_id);
    return `工程別: ${wd ? wd.name : "(未設定)"}`;
  }
  return "施工(全般)";
};

// ===== 品質測定項目マスタ =====
export const qualityMeasurementItemConfig = defineMasterConfig({
  collectionKey: "qualityMeasurementItems",
  title: "品質測定項目",
  idPrefix: "qmi",
  newDefaults: () => ({
    area_division_id: 0, name: "", unit: "", scope_kind: "construction",
    material_id: null, work_type_id: null, work_subtype_id: null, work_detail_id: null,
    upper_limit: null, lower_limit: null, judge_required: false, timing_category: null,
  }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "area_division_id", label: "工事型区分", width: 140, render: (r, db) => {
      const area = db.areaDivisions.find((d) => d.id === r.area_division_id);
      return area ? `#${area.id} ${area.name}` : `#${r.area_division_id}`;
    }},
    { key: "name", label: "項目名" },
    { key: "unit", label: "単位", width: 80 },
    { key: "scope_kind", label: "適用対象", width: 200, render: (r, db) => qualityScopeLabel(r, db) },
    { key: "lower_limit", label: "下限", width: 80 },
    { key: "upper_limit", label: "上限", width: 80 },
    { key: "judge_required", label: "判定必須", width: 80, render: (r) => yesNo(r.judge_required) },
    { key: "timing_category", label: "タイミング", width: 140, render: (r) => r.timing_category ?? "-" },
  ],
  fields: [
    { key: "area_division_id", label: "工事型区分", type: "select", required: true, options: areaSelectFromDb },
    { key: "name", label: "項目名", type: "text", required: true, helperText: WORD.mastersConfigs.hQualityName },
    { key: "unit", label: "単位", type: "text", helperText: WORD.mastersConfigs.hQualityUnit },
    { key: "scope_kind", label: "スコープ", type: "select", required: true, options: [
      { value: "construction", label: "施工(全般)" },
      { value: "material", label: "材料別" },
      { value: "work_type", label: "工種別" },
      { value: "work_subtype", label: "種別別" },
      { value: "work_detail", label: "工程別" },
    ]},
    { key: "material_id", label: "対象材料", type: "select", visible: (draft) => draft.scope_kind === "material", options: materialLinkOptions },
    { key: "work_type_id", label: "対象工種", type: "select", visible: (draft) => draft.scope_kind === "work_type", options: workTypeLinkOptions },
    { key: "work_subtype_id", label: "対象種別", type: "select", visible: (draft) => draft.scope_kind === "work_subtype", options: workSubtypeLinkOptions },
    { key: "work_detail_id", label: "対象工程", type: "select", visible: (draft) => draft.scope_kind === "work_detail", options: workDetailLinkOptions },
    { key: "lower_limit", label: "下限値", type: "number" },
    { key: "upper_limit", label: "上限値", type: "number" },
    { key: "judge_required", label: "判定必須", type: "boolean" },
    { key: "timing_category", label: "測定タイミング", type: "text", helperText: WORD.mastersConfigs.hQualityTiming },
  ],
});

// ===== 撹拌方法マスタ =====
export const stirringMethodConfig = defineMasterConfig({
  collectionKey: "stirringMethods",
  title: "撹拌方法",
  description: "ハンドミキサー／ミキサー／手練り 等。配合比から参照される従属マスタ。",
  idPrefix: "sm",
  newDefaults: () => ({ material_division_id: 1, name: "" }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "name", label: "名前" },
  ],
  fields: [
    { key: "material_division_id", label: "材料区分", type: "select", required: true, options: materialSelectFromDb },
    { key: "name", label: "名前", type: "text", required: true },
  ],
});

// ===== 施工方法マスタ =====
export const constructionMethodConfig = defineMasterConfig({
  collectionKey: "constructionMethods",
  title: "施工方法",
  description: "左官／シール／吹付／注入／塗布 等。配合比から参照される従属マスタ。",
  idPrefix: "cm",
  newDefaults: () => ({ material_division_id: 1, name: "" }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "name", label: "名前" },
  ],
  fields: [
    { key: "material_division_id", label: "材料区分", type: "select", required: true, options: materialSelectFromDb },
    { key: "name", label: "名前", type: "text", required: true },
  ],
});

// ===== 材料マスタ(ベーシック) =====
// rev: 専用ページ(MaterialMasterPage)で MaterialDetail と統合編集する想定。本configは簡易CRUD。
export const materialConfig = defineMasterConfig({
  collectionKey: "materials",
  title: "材料",
  idPrefix: "m",
  newDefaults: () => ({
    material_division_id: 1, name: "", usage_category: "", standard_name: "", manufacturer: "",
    package_form: "", display_order: 99, is_active: true, qr_managed: true,
    humidity_upper: null, humidity_lower: null,
  }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "name", label: "名前" },
    { key: "usage_category", label: "用途", width: 120 },
    { key: "manufacturer", label: "メーカー", width: 140 },
    { key: "package_form", label: "荷姿", width: 100 },
    { key: "qr_managed", label: "QR管理", width: 80, render: (r) => yesNo(r.qr_managed) },
    { key: "is_active", label: "有効", width: 80, render: (r) => yesNo(r.is_active) },
  ],
  fields: [
    { key: "material_division_id", label: "材料区分", type: "select", required: true, options: materialSelectFromDb },
    { key: "name", label: "名前", type: "text", required: true },
    { key: "usage_category", label: "用途", type: "text" },
    { key: "standard_name", label: "規格名", type: "text" },
    { key: "manufacturer", label: "メーカー", type: "text" },
    { key: "package_form", label: "荷姿", type: "text" },
    { key: "display_order", label: "表示順", type: "number" },
    { key: "qr_managed", label: "QR管理対象", type: "boolean", helperText: WORD.materialMaster.hQrManaged },
    { key: "is_active", label: "有効", type: "boolean" },
    { key: "humidity_lower", label: "湿度下限", type: "number" },
    { key: "humidity_upper", label: "湿度上限", type: "number" },
  ],
});

// ===== 材料細目マスタ =====
export const materialDetailConfig = defineMasterConfig({
  collectionKey: "materialDetails",
  title: "材料細目",
  idPrefix: "mdt",
  newDefaults: () => ({
    material_division_id: 1, material_id: 0, detail_name: "",
    package_form: "", shelf_life_months: null, content_quantity: null, content_unit: "",
    sort_order: 99, specific_gravity: null, hazardous_class: null,
  }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "material_id", label: "材料", width: 160, render: (r, db) => {
      const m = db.materials.find((d) => d.id === r.material_id);
      return m ? `#${m.id} ${m.name}` : `#${r.material_id}`;
    }},
    { key: "detail_name", label: "細目名" },
    { key: "package_form", label: "荷姿", width: 100 },
    { key: "content_quantity", label: "内容量", width: 100 },
    { key: "content_unit", label: "単位", width: 80 },
    { key: "sort_order", label: "ソート", width: 80 },
  ],
  fields: [
    { key: "material_division_id", label: "材料区分", type: "select", required: true, options: materialSelectFromDb },
    { key: "material_id", label: "材料", type: "select", required: true, options: (db) =>
      db.materials.filter(isActive).map((m) => ({ value: m.id, label: `#${m.id} ${m.name}` }))
    },
    { key: "detail_name", label: "細目名", type: "text", required: true },
    { key: "package_form", label: "荷姿", type: "text" },
    { key: "shelf_life_months", label: "使用期限(月)", type: "number" },
    { key: "content_quantity", label: "内容量", type: "number" },
    { key: "content_unit", label: "容量単位", type: "text" },
    { key: "sort_order", label: "ソート順", type: "number" },
    { key: "specific_gravity", label: "比重", type: "number" },
    { key: "hazardous_class", label: "危険物分類", type: "text" },
  ],
});

// ===== 可使時間マスタ =====
export const potLifeSpecConfig = defineMasterConfig({
  collectionKey: "potLifeSpecs",
  title: "可使時間",
  description: "材料 × 温度区間ごとの可使時間(分)を定義。登録が無い材料は「温度・可使時間とも制限なし」とみなします。",
  idPrefix: "pl",
  newDefaults: () => ({
    material_division_id: 1, material_id: 0, definition_name: "",
    temperature_lower: null, temperature_upper: null, pot_life_minutes: null,
  }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "material_id", label: "材料", width: 160, render: (r, db) => {
      const m = db.materials.find((d) => d.id === r.material_id);
      return m ? `#${m.id} ${m.name}` : `#${r.material_id}`;
    }},
    { key: "definition_name", label: "定義名" },
    {
      key: "temperature_lower", label: "温度区間(℃)", width: 160,
      render: (r) => `${r.temperature_lower ?? "-∞"} 〜 ${r.temperature_upper ?? "+∞"}`,
    },
    {
      key: "pot_life_minutes", label: "可使時間(分)", width: 110,
      render: (r) => {
        if (r.pot_life_minutes == null) {
          return "制限なし";
        }
        return `${r.pot_life_minutes}`;
      },
    },
  ],
  fields: [
    { key: "material_division_id", label: "材料区分", type: "select", required: true, options: materialSelectFromDb },
    { key: "material_id", label: "材料", type: "select", required: true, options: (db) =>
      db.materials.filter(isActive).map((m) => ({ value: m.id, label: `#${m.id} ${m.name}` }))
    },
    { key: "definition_name", label: "定義名", type: "text", required: true, placeholder: "例: 標準", helperText: WORD.mastersConfigs.hPotLifeDefinitionName },
    { key: "temperature_lower", label: "温度区間 下限(℃)", type: "number", helperText: WORD.mastersConfigs.hPotLifeTempLower },
    { key: "temperature_upper", label: "温度区間 上限(℃)", type: "number", helperText: WORD.mastersConfigs.hPotLifeTempUpper },
    { key: "pot_life_minutes", label: "可使時間(分)", type: "number", helperText: WORD.mastersConfigs.hPotLifeMinutes },
  ],
});

// ===== 配合比定義マスタ =====
export const mixRatioDefinitionConfig = defineMasterConfig({
  collectionKey: "mixRatioDefinitions",
  title: "配合比",
  idPrefix: "mrd",
  newDefaults: () => ({
    material_division_id: 1, material_id: 0, stirring_time_minutes: 3,
    stirring_method_id: null, construction_method_id: null, remarks: null,
  }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "material_id", label: "材料", width: 160, render: (r, db) => {
      const m = db.materials.find((d) => d.id === r.material_id);
      return m ? `#${m.id} ${m.name}` : `#${r.material_id}`;
    }},
    { key: "stirring_time_minutes", label: "撹拌時間(分)", width: 120 },
    { key: "stirring_method_id", label: "撹拌方法", width: 140, render: (r, db) => {
      if (r.stirring_method_id == null) return "-";
      const sm = db.stirringMethods.find((d) => d.id === r.stirring_method_id);
      return sm ? `#${sm.id} ${sm.name}` : `#${r.stirring_method_id}`;
    }},
    { key: "construction_method_id", label: "施工方法", width: 140, render: (r, db) => {
      if (r.construction_method_id == null) return "-";
      const cm = db.constructionMethods.find((d) => d.id === r.construction_method_id);
      return cm ? `#${cm.id} ${cm.name}` : `#${r.construction_method_id}`;
    }},
  ],
  fields: [
    { key: "material_division_id", label: "材料区分", type: "select", required: true, options: materialSelectFromDb },
    { key: "material_id", label: "材料", type: "select", required: true, options: (db) =>
      db.materials.filter(isActive).map((m) => ({ value: m.id, label: `#${m.id} ${m.name}` }))
    },
    { key: "stirring_time_minutes", label: "撹拌時間(分)", type: "number", required: true },
    { key: "stirring_method_id", label: "撹拌方法", type: "select", options: (db) =>
      [{ value: "", label: "(なし)" }, ...db.stirringMethods.filter(isActive).map((s) => ({ value: s.id, label: `#${s.id} ${s.name}` }))]
    },
    { key: "construction_method_id", label: "施工方法", type: "select", options: (db) =>
      [{ value: "", label: "(なし)" }, ...db.constructionMethods.filter(isActive).map((c) => ({ value: c.id, label: `#${c.id} ${c.name}` }))]
    },
    { key: "remarks", label: "備考", type: "multiline" },
  ],
});

// ===== 配合比明細マスタ =====
export const mixRatioDetailConfig = defineMasterConfig({
  collectionKey: "mixRatioDetails",
  title: "配合比明細",
  description: "配合比 × 構成細目 × 配合比範囲。",
  idPrefix: "mrt",
  newDefaults: () => ({
    material_division_id: 1, material_id: 0, mix_ratio_definition_id: 0, material_detail_id: 0,
    ratio_start: 0, ratio_end: null,
  }),
  columns: [
    { key: "id", label: "ID", width: 60 },
    { key: "mix_ratio_definition_id", label: "配合比", width: 100, render: (r) => `#${r.mix_ratio_definition_id}` },
    { key: "material_detail_id", label: "構成細目", width: 200, render: (r, db) => {
      const md = db.materialDetails.find((d) => d.id === r.material_detail_id);
      return md ? `#${md.id} ${md.detail_name}` : `#${r.material_detail_id}`;
    }},
    { key: "ratio_start", label: "配合比", width: 120, render: (r) => <Typography fontFamily="monospace">{formatRatioRange(r.ratio_start, r.ratio_end, " 〜 ")}</Typography> },
  ],
  fields: [
    { key: "material_division_id", label: "材料区分", type: "select", required: true, options: materialSelectFromDb },
    { key: "mix_ratio_definition_id", label: "配合比", type: "select", required: true, options: (db) =>
      db.mixRatioDefinitions.filter(isActive).map((m) => ({ value: m.id, label: `#${m.id}` }))
    },
    { key: "material_detail_id", label: "構成細目", type: "select", required: true, options: (db) =>
      db.materialDetails.filter(isActive).map((d) => ({ value: d.id, label: `#${d.id} ${d.detail_name}` }))
    },
    { key: "ratio_start", label: "配合比 開始値", type: "number", required: true },
    { key: "ratio_end", label: "配合比 終了値(任意)", type: "number" },
  ],
});

// ===== 全マスタ設定の登録 =====
// MasterListPage のキー → config 解決用のリスト
// 専用 UI (WorkClassificationPage / MaterialMasterPage / MixRatioMasterPage) を持つマスタは
// 汎用 CRUD 配列から除外。MasterListPage 側で旧キーアクセス時に専用ページへリダイレクトする。

// config のコールバックは全てメソッド構文(バイバリアント検査)のため、行型付き config を
// 消去型(MasterCrudConfig)の一覧へ表明なしで直接格納できる。
export const allMasterConfigs: Array<{ key: string; config: MasterCrudConfig }> = [
  { key: "area-division", config: areaDivisionConfig },
  { key: "material-division", config: materialDivisionConfig },
  { key: "client", config: clientConfig },
  { key: "carry-in-location", config: carryInLocationConfig },
  { key: "location-division", config: locationDivisionConfig },
  { key: "quality-measurement-item", config: qualityMeasurementItemConfig },
  { key: "stirring-method", config: stirringMethodConfig },
  { key: "construction-method", config: constructionMethodConfig },
  { key: "pot-life", config: potLifeSpecConfig },
];
